import React, { useEffect, useState, lazy, Suspense, useMemo } from "react";
import { useInView } from "react-intersection-observer";
import { Helmet } from "react-helmet";
import { useAppDispatch, useAppSelector } from "app/hooks";
import {
  getEditorBlogs,
  getTop10Categories,
  selectBlogState,
  getTop10Authors,
  getNewestAuthor,
  searchBlogs,
} from "app/blogs/blogs";
import { PostDataType, PostAuthorType } from "data/types";
import { Pageable, BlogResponseType } from "types/Blog";
import { PostDataConverter } from "app/modals/PostDataConverter";
import { AuthorConverter } from "app/modals/AuthorConverter";
import { AccessPlatform } from "../../enum";
import { Alert } from "components";
import BgGlassmorphism from "components/BgGlassmorphism/BgGlassmorphism";
import LatestPostWrapper from "./LatestPostWrapper";
import SectionSliderPosts from "./SectionSliderPosts";
import { DEMO_CATEGORIES } from "data/taxonomies";
import BackgroundSection from "components/BackgroundSection/BackgroundSection";
import SkeletonDynamic from "components/Skeleton/Skeleton";

const SectionLargeSlider = lazy(() => import("./SectionLargeSlider"));
const SectionLargeSliderAuthor = lazy(() => import("components/SectionSliderNewAthors/SectionSliderNewAuthors"));
const SectionSliderNewCategories = lazy(() => import("components/SectionSliderNewCategories/SectionSliderNewCategories"));
const SectionMagazine1 = lazy(() => import("./SectionMagazine1"));
const SectionGridAuthorBox = lazy(() => import("components/SectionGridAuthorBox/SectionGridAuthorBox"));
const SectionBecomeAnAuthor = lazy(() => import("components/SectionBecomeAnAuthor/SectionBecomeAnAuthor"));
const SectionSubscribe2 = lazy(() => import("components/SectionSubscribe2/SectionSubscribe2"));

const PageHome: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [authors, setAuthors] = useState<PostAuthorType[]>([]);
  const [newestAuthors, setNewestAuthors] = useState<PostAuthorType[]>([]);
  const [tabs, setTabs] = useState(["all"]);
  const [latestArticles, setLatestArticles] = useState<PostDataType[]>([]);
  const [popularPosts, setPopularPosts] = useState<PostDataType[]>([]);
  const [allArticles, setAllArticles] = useState<PostDataType[]>([]);
  const [data, setData] = useState<PostDataType[]>([]);
  const [error, setError] = useState('');
  const dispatch = useAppDispatch();
  const [page, setPage] = useState<Pageable>({ pageNo: 0, pageSize: 20 });
  const { trendingTopic } = useAppSelector(selectBlogState);


  const topAuthors = useMemo(() =>  authors.filter((_, i) => i < 10), [authors]);
  const topNewestAuthors = useMemo(() => newestAuthors.slice(0, 10), [newestAuthors]);

  const fetchPopularPost = () => {
    const params = {
      pageNo: `${page.pageNo}`,
      pageSize: `${page.pageSize}`,
      orders: `totalViewer|desc||publishedDate|desc`,
      filterAnd : `status|eq|APPROVED&accessPlatform|in|${AccessPlatform.INSIGHTS}`
    }
    const urlPath= new URLSearchParams(params).toString();
    searchBlogs(urlPath)
      .then((res)=>{
        const _post = res.content.map((val:BlogResponseType)=>{
          return new PostDataConverter(val)
        })
        setPopularPosts(_post.filter((_, i) => i >= 0 && i < 7));
        })
      .catch((err) => setError(err?.message || "Something wrong, Please try again."));
  }
  
  useEffect(() => {
    fetchPopularPost();
  }, [])
  
  useEffect(() => {
    setLoading(true)
    dispatch(getTop10Categories())
      .unwrap()
      .then((res) => {
        setLoading(false)
        const recentCategories = res.recent.slice(0,3)
        setTabs((prev) => {
          return [
            ...prev,
            ...recentCategories.map((cat) => {
              return cat.name;
            }),
          ];
        });
      })
      .catch((err) => {
        setLoading(false)
        setError(err?.message || "Something wrong, Please try again.")
      });
    const params = {
      pageNo: `${page.pageNo}`,
      pageSize: `${page.pageSize}`,
      orders: `publishedDate|desc||totalViewer|desc`,
      filterAnd : `status|eq|APPROVED&accessPlatform|in|${AccessPlatform.INSIGHTS}`
    }
    const urlPath= new URLSearchParams(params).toString();
    searchBlogs(urlPath)
      .then((res)=>{
        const _post = res.content.map((val:BlogResponseType)=>{
          return new PostDataConverter(val)
        })
          setLatestArticles(_post.filter((_, i) => i >= 0 && i < 7));
          setAllArticles(_post)
        })
      .catch((err) => setError(err?.message || "Something wrong, Please try again."));
      const param = {
        pageNo: 0,
        pageSize: 10,
      }
    getTop10Authors(param)
      .then((res) => {
        const _post = res.content.map((val)=>{
          return new AuthorConverter(val)
        })
        setAuthors(_post);
      })
      .catch((err) => setError(err?.message || "Something wrong, Please try again."));      
    getEditorBlogs(page)
      .then((res:PostDataType[]) =>{
       setData(res);
      })
      .catch((err) => setError(err?.message || "Something wrong, Please try again."));

      getNewestAuthor()
      .then((res) =>{
        setNewestAuthors(res)
      })
      .catch((err) =>{
        setError(err?.message || "Something wrong, Please try again.")
      })
  }, []);


  // Handle Category Update
  function changeCategory(id:string | undefined) {
    const params = {
      pageNo: `${page.pageNo}`,
      pageSize: `${page.pageSize}`,
      orders: id ? `publishedDate|desc` : `publishedDate|desc||totalViewer|desc`,
      filterAnd: id ? `status|eq|APPROVED&accessPlatform|eq|INSIGHTS&status|eq|APPROVED&category|eq|${id}` : `status|eq|APPROVED&accessPlatform|in|${AccessPlatform.INSIGHTS}`
    }
    const urlPath= new URLSearchParams(params).toString();
    searchBlogs(urlPath)
      .then((res)=>{
        const _post = res.content.map((val:BlogResponseType)=>{
          return new PostDataConverter(val)
        })
          setLatestArticles(_post.filter((_, i) => i >= 0 && i < 7));        
      })
      .catch((err) => setError(err?.message || "Something wrong, Please try again."));
  }

  // Lazy load triggers
  const [refAuthors, inViewAuthors] = useInView({ triggerOnce: true, rootMargin: "200px 0px" });
  const [refCats, inViewCats] = useInView({ triggerOnce: true, rootMargin: "200px 0px" });
  const [refMag, inViewMag] = useInView({ triggerOnce: true, rootMargin: "200px 0px" });
  const [refGridAuthor, inViewGridAuthor] = useInView({ triggerOnce: true, rootMargin: "200px 0px" });
  const [refBecome, inViewBecome] = useInView({ triggerOnce: true, rootMargin: "200px 0px" });

  return (
    <>
      <div className="nc-PageHome relative">
        <Helmet>
        <title>DZ Insights- Discover The World</title>
        <link rel = "canonical" href = {window.location.href} />
        </Helmet>

        {error && <Alert type="error" children={error} onClose={() => setError("")} />}
      {/* ======== ALL SECTIONS ======== */}

        <div className="relative overflow-hidden">
      {/* ======== BG GLASS ======== */}

          <BgGlassmorphism />
        {/* ======= START CONTAINER ============= */}

          <div className="container relative">
          {/* === SECTION  === */}

            <div className="min-h-[750px] sm:min-h-[780px] md:min-h-[780px] lg:min-h-[860px]">
              <Suspense fallback={<SkeletonDynamic type="largeSlider"/>}>
                {data.length > 0 ? (
                  <SectionLargeSlider
                    className="pt-10 pb-16 md:py-16 lg:pb-28 lg:pt-20"
                    posts={data}
                  />
                ) : (
                  <SkeletonDynamic type="largeSlider" />
                )}
              </Suspense>
            </div>
          {/* === SECTION  === */}

            <div className="relative py-16 min-h-[480px]" ref={refAuthors}>
              <BackgroundSection />
              {inViewAuthors ? (
                <Suspense fallback={<SkeletonDynamic type="newestAuthorSection" />}>
                   {loading || topNewestAuthors.length === 0 ? (
                    <SkeletonDynamic type="newestAuthorSection" />
                  ) : (
                  <SectionLargeSliderAuthor
                    heading="Newest authors"
                    subHeading="Say hello to future creator potentials"
                    authors={topNewestAuthors}
                    uniqueSliderClass="PageHome"
                  />
                  )}
                </Suspense>
              ) : (
                <SkeletonDynamic type="newestAuthorSection" />
              )}
            </div>

            <div ref={refCats} className="min-h-[380px]">
              {inViewCats ? (
                <Suspense fallback={<SkeletonDynamic type="TopTrendingTopics" />}>
                  {loading || trendingTopic.length === 0 ? (
                   <SkeletonDynamic type="TopTrendingTopics" />
                    ) : (
                  <SectionSliderNewCategories
                    className="py-16 lg:py-28"
                    heading="Top trending topics"
                    subHeading={`Discover ${trendingTopic.length} topics`}
                    categories={trendingTopic.slice(0, 10)}
                    categoryCardType="card4"
                    uniqueSliderClass="pageHome-section5"
                  />
                   )}
                </Suspense>
              ) : (
                <SkeletonDynamic type="TopTrendingTopics" />
              )}
            </div>

             <div ref={refMag} className="min-h-[450px]">
              {inViewMag ? (
                <Suspense fallback={<SkeletonDynamic type="LatestArticles" />}>
                  {loading || latestArticles.length === 0 ? (
                    <SkeletonDynamic type="LatestArticles" />
                    ) : (
                  <SectionMagazine1
                    className="py-16 lg:py-28"
                    posts={latestArticles}
                    tabs={tabs}
                    onUpdate={changeCategory}
                  />
                    )}
                </Suspense>
              ) : (
                <SkeletonDynamic type="LatestArticles" />
              )}
            </div>
          </div> 

          <div className="container">

            <div ref={refGridAuthor} className="min-h-[400px]">
              {inViewGridAuthor ? (
                <Suspense fallback={<SkeletonDynamic type="topAuthorSection" />}>
                  {loading || topAuthors.length === 0 ? (
                    <SkeletonDynamic type="topAuthorSection" />
                    ) : (
                  <SectionGridAuthorBox
                    className="py-16 lg:py-28"
                    heading="Top 10 author of the month"
                    subHeading="Rating based on customer reviews"
                    authors={topAuthors}
                    />
                    )}
                </Suspense>
              ) : (
                <SkeletonDynamic type="topAuthorSection" />
              )}
            </div> 

            <div className="relative py-16 min-h-[400px]" ref={refBecome}>
              <BackgroundSection />
              {inViewBecome ? (
                <Suspense fallback={<SkeletonDynamic type="becomeAnAuthor" />}>
                  <SectionBecomeAnAuthor />
                </Suspense>
              ) : (
                <SkeletonDynamic type="becomeAnAuthor" />
              )}
            </div>

            <SectionSliderPosts
              postCardName="card11"
              heading="More Articles"
              subHeading={`Over ${allArticles.length} articles`}
              posts={allArticles.filter(
                (p, i) => i >= 0 && i < 20 && p.postType === "standard"
              )}              
              sliderStype="style2"
              uniqueSliderClass="pageHome-section12"
              className="py-16 lg:py-28"
            />

          <LatestPostWrapper      
            widgetPosts={popularPosts.filter((_, i) => i >= 0 && i < 7)}
            categories={DEMO_CATEGORIES.filter((_, i) => i >= 0 && i < 8)}
            tags={DEMO_CATEGORIES}
            // authors={authors.filter((_, i) => i >= 0 && i < 10)}
            authors={topAuthors}
          />
          {/* === SECTION 12 === */}
          <div className="relative">
            <Suspense fallback={<SkeletonDynamic type="subscribeSection" />}>
              <SectionSubscribe2 className="pb-20" />
            </Suspense>
          </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default PageHome;